import React, { useState } from "react";
import { Flag } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useB2C } from "@/context/B2CAuthContext";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FindoSpinner } from "@/components/FindoSpinner";

const REASONS = [
  { v: "inappropriate", k: "rp.inappropriate" },
  { v: "duplicate", k: "rp.duplicate" },
  { v: "wrong_category", k: "rp.wrongCategory" },
  { v: "other", k: "rp.other" },
];

// Discreet "Report" control for a public ad. Visible only to logged-in B2C users.
// Single-select reason (radios); one report per user per ad (backend idempotent).
export function ReportAdButton({ adId, className = "" }) {
  const { user } = useB2C();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("inappropriate");
  const [comment, setComment] = useState("");
  const needsComment = reason === "other";
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  async function submit(e) {
    e.preventDefault();
    if (needsComment && !comment.trim()) { toast.error(t("rp.otherRequired")); return; }
    setBusy(true);
    try {
      const res = await api(`/public-ads/${adId}/report`, { method: "POST", b2c: true, body: { reason, comment: needsComment ? comment.trim() : null } });
      if (res?.already) toast.info(t("rp.already"));
      else toast.success(t("rp.sent"));
      setOpen(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
        className={`inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-findo-red transition-colors ${className}`}
        data-testid="report-open-btn"
        aria-label={t("rp.report")}
      >
        <Flag size={12} /> {t("rp.report")}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="report-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle icon={Flag} className="font-display">{t("rp.title")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="findo-numbered space-y-5">
            <div className="space-y-2" role="radiogroup">
              {REASONS.map((r) => (
                <label
                  key={r.v}
                  className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${
                    reason === r.v ? "border-findo-red bg-findo-red/5" : "border-border hover:bg-slate-50"
                  }`}
                  data-testid={`report-reason-${r.v}`}
                >
                  <input
                    type="radio"
                    name="report-reason"
                    value={r.v}
                    checked={reason === r.v}
                    onChange={() => setReason(r.v)}
                    className="accent-findo-red"
                  />
                  <span className="text-sm text-slate-700">{t(r.k)}</span>
                </label>
              ))}
              {needsComment && (
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} required maxLength={500} rows={3} autoFocus
                  placeholder={t("rp.otherPh")}
                  className="w-full rounded-xl border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-findo-red/30 resize-none"
                  data-testid="report-other-text" />
              )}
            </div>
            <Button
              type="submit"
              disabled={busy || (needsComment && !comment.trim())}
              className="w-full rounded-xl font-semibold bg-findo-red hover:bg-findo-red/90"
              data-testid="report-submit"
            >
              {busy ? <FindoSpinner size={16} /> : t("rp.send")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ReportAdButton;
